import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import './PoolAnalyticsModal.css';

const PoolAnalyticsModal = ({ isOpen, onClose, pool }) => {
  const { showToast } = useToast();
  const [analytics, setAnalytics] = useState({
    performance: {
      daily: [],
      monthly: [],
      cumulative: []
    },
    trades: [],
    metrics: {
      totalReturn: 0,
      sharpeRatio: 0,
      maxDrawdown: 0,
      volatility: 0,
      winRate: 0,
      avgTradeReturn: 0
    },
    fees: {
      managementFeesCollected: 0,
      performanceFeesCollected: 0,
      totalFeesCollected: 0
    },
    positions: []
  });
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('performance');
  const [timeframe, setTimeframe] = useState('30d');

  useEffect(() => {
    if (isOpen && pool) {
      fetchAnalytics();
    }
  }, [isOpen, pool, timeframe]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/pools/${pool.id}/analytics?timeframe=${timeframe}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      } else {
        throw new Error('Failed to fetch analytics');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      showToast('Failed to load analytics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async () => {
    try {
      const response = await fetch(`/api/admin/pools/${pool.id}/export-report?timeframe=${timeframe}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${pool.name}_analytics_${timeframe}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showToast('Report exported successfully', 'success');
      } else {
        throw new Error('Failed to export report');
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      showToast('Failed to export report', 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const getPerformanceColor = (value) => {
    if (value > 0) return 'positive';
    if (value < 0) return 'negative';
    return 'neutral';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="pool-analytics-modal">
        <div className="modal-header">
          <div className="header-content">
            <h2>Pool Analytics - {pool.name}</h2>
            <div className="header-controls">
              <select 
                value={timeframe} 
                onChange={(e) => setTimeframe(e.target.value)}
                className="timeframe-selector"
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="1y">Last Year</option>
                <option value="all">All Time</option>
              </select>
              <button className="export-btn" onClick={exportReport}>
                Export Report
              </button>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-btn ${activeTab === 'performance' ? 'active' : ''}`}
            onClick={() => setActiveTab('performance')}
          >
            Performance
          </button>
          <button
            className={`tab-btn ${activeTab === 'trades' ? 'active' : ''}`}
            onClick={() => setActiveTab('trades')}
          >
            Trading Activity
          </button>
          <button
            className={`tab-btn ${activeTab === 'positions' ? 'active' : ''}`}
            onClick={() => setActiveTab('positions')}
          >
            Current Positions
          </button>
          <button
            className={`tab-btn ${activeTab === 'fees' ? 'active' : ''}`}
            onClick={() => setActiveTab('fees')}
          >
            Fee Analysis
          </button>
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading analytics...</p>
            </div>
          ) : (
            <>
              {activeTab === 'performance' && (
                <div className="performance-tab">
                  {/* Key Metrics */}
                  <div className="metrics-grid">
                    <div className="metric-card">
                      <div className="metric-label">Total Return</div>
                      <div className={`metric-value ${getPerformanceColor(analytics.metrics.totalReturn)}`}>
                        {formatPercentage(analytics.metrics.totalReturn)}
                      </div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Sharpe Ratio</div>
                      <div className="metric-value">
                        {analytics.metrics.sharpeRatio.toFixed(2)}
                      </div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Max Drawdown</div>
                      <div className="metric-value negative">
                        {formatPercentage(analytics.metrics.maxDrawdown)}
                      </div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Volatility</div>
                      <div className="metric-value">
                        {formatPercentage(analytics.metrics.volatility)}
                      </div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Win Rate</div>
                      <div className="metric-value">
                        {formatPercentage(analytics.metrics.winRate)}
                      </div>
                    </div>
                    <div className="metric-card">
                      <div className="metric-label">Avg Trade Return</div>
                      <div className={`metric-value ${getPerformanceColor(analytics.metrics.avgTradeReturn)}`}>
                        {formatPercentage(analytics.metrics.avgTradeReturn)}
                      </div>
                    </div>
                  </div>

                  {/* Performance Chart */}
                  <div className="performance-chart-section">
                    <h3>Performance Over Time</h3>
                    <div className="chart-container">
                      {analytics.performance.daily.length > 0 ? (
                        <div className="simple-chart">
                          {analytics.performance.daily.map((point, index) => (
                            <div key={index} className="chart-point">
                              <div className="point-date">
                                {new Date(point.date).toLocaleDateString()}
                              </div>
                              <div className={`point-value ${getPerformanceColor(point.return)}`}>
                                {formatPercentage(point.cumulativeReturn)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-data">No performance data available</div>
                      )}
                    </div>
                  </div>

                  {/* Monthly Performance */}
                  <div className="monthly-performance">
                    <h3>Monthly Performance</h3>
                    <div className="monthly-grid">
                      {analytics.performance.monthly.map((month, index) => (
                        <div key={index} className="monthly-item">
                          <div className="month-label">
                            {new Date(month.month).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short' 
                            })}
                          </div>
                          <div className={`month-return ${getPerformanceColor(month.return)}`}>
                            {formatPercentage(month.return)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'trades' && (
                <div className="trades-tab">
                  <div className="trades-summary">
                    <div className="summary-stats">
                      <div className="stat">
                        <span className="stat-label">Total Trades</span>
                        <span className="stat-value">{analytics.trades.length}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Winning Trades</span>
                        <span className="stat-value positive">
                          {analytics.trades.filter(t => t.pnl > 0).length}
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Losing Trades</span>
                        <span className="stat-value negative">
                          {analytics.trades.filter(t => t.pnl < 0).length}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="trades-list">
                    <div className="trades-table">
                      <div className="table-header">
                        <div className="header-cell">Date</div>
                        <div className="header-cell">Type</div>
                        <div className="header-cell">Token</div>
                        <div className="header-cell">Amount</div>
                        <div className="header-cell">Price</div>
                        <div className="header-cell">P&L</div>
                        <div className="header-cell">Strategy</div>
                      </div>
                      
                      {analytics.trades.map((trade, index) => (
                        <div key={index} className="table-row">
                          <div className="cell">
                            {new Date(trade.executedAt).toLocaleDateString()}
                          </div>
                          <div className={`cell trade-type-${trade.tradeType}`}>
                            {trade.tradeType.toUpperCase()}
                          </div>
                          <div className="cell">
                            {trade.tokenSymbol}
                          </div>
                          <div className="cell">
                            {formatCurrency(trade.amount)}
                          </div>
                          <div className="cell">
                            ${trade.price?.toFixed(6) || 'N/A'}
                          </div>
                          <div className={`cell ${getPerformanceColor(trade.pnl || 0)}`}>
                            {trade.pnl ? formatPercentage(trade.pnl) : 'N/A'}
                          </div>
                          <div className="cell strategy-reason">
                            {trade.strategyReason || 'N/A'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'positions' && (
                <div className="positions-tab">
                  <div className="positions-summary">
                    <div className="summary-stats">
                      <div className="stat">
                        <span className="stat-label">Total Positions</span>
                        <span className="stat-value">{analytics.positions.length}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Total Value</span>
                        <span className="stat-value">
                          {formatCurrency(analytics.positions.reduce((sum, pos) => sum + pos.usdValue, 0))} USD
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="positions-list">
                    {analytics.positions.length === 0 ? (
                      <div className="no-positions">
                        <p>No current positions</p>
                      </div>
                    ) : (
                      <div className="positions-table">
                        <div className="table-header">
                          <div className="header-cell">Token</div>
                          <div className="header-cell">Balance</div>
                          <div className="header-cell">Avg Buy Price</div>
                          <div className="header-cell">Current Price</div>
                          <div className="header-cell">USD Value</div>
                          <div className="header-cell">P&L</div>
                          <div className="header-cell">Allocation</div>
                        </div>
                        
                        {analytics.positions.map((position, index) => (
                          <div key={index} className="table-row">
                            <div className="cell">
                              <div className="token-info">
                                <span className="token-symbol">{position.tokenSymbol}</span>
                                <span className="token-name">{position.tokenName}</span>
                              </div>
                            </div>
                            <div className="cell">
                              {formatCurrency(position.balance)}
                            </div>
                            <div className="cell">
                              ${position.avgBuyPrice?.toFixed(6) || 'N/A'}
                            </div>
                            <div className="cell">
                              ${position.currentPrice?.toFixed(6) || 'N/A'}
                            </div>
                            <div className="cell">
                              ${formatCurrency(position.usdValue)}
                            </div>
                            <div className={`cell ${getPerformanceColor(position.pnlPercentage || 0)}`}>
                              {position.pnlPercentage ? formatPercentage(position.pnlPercentage) : 'N/A'}
                            </div>
                            <div className="cell">
                              {position.allocationPercentage?.toFixed(1) || '0.0'}%
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'fees' && (
                <div className="fees-tab">
                  <div className="fees-summary">
                    <div className="summary-stats">
                      <div className="stat">
                        <span className="stat-label">Management Fees</span>
                        <span className="stat-value">
                          {formatCurrency(analytics.fees.managementFeesCollected)} SOL
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Performance Fees</span>
                        <span className="stat-value">
                          {formatCurrency(analytics.fees.performanceFeesCollected)} SOL
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Total Fees</span>
                        <span className="stat-value">
                          {formatCurrency(analytics.fees.totalFeesCollected)} SOL
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="fee-breakdown">
                    <h3>Fee Structure</h3>
                    <div className="fee-details">
                      <div className="fee-item">
                        <span className="fee-label">Management Fee Rate:</span>
                        <span className="fee-value">{pool.managementFee}% annually</span>
                      </div>
                      <div className="fee-item">
                        <span className="fee-label">Performance Fee Rate:</span>
                        <span className="fee-value">{pool.performanceFee}% of profits</span>
                      </div>
                      <div className="fee-item">
                        <span className="fee-label">Fee Collection Method:</span>
                        <span className="fee-value">Automatic deduction from pool assets</span>
                      </div>
                    </div>
                  </div>

                  <div className="fee-projections">
                    <h3>Fee Projections</h3>
                    <div className="projection-grid">
                      <div className="projection-item">
                        <span className="projection-label">Estimated Annual Management Fees:</span>
                        <span className="projection-value">
                          {formatCurrency((pool.totalSize * pool.managementFee) / 100)} SOL
                        </span>
                      </div>
                      <div className="projection-item">
                        <span className="projection-label">Performance Fee (if 20% return):</span>
                        <span className="projection-value">
                          {formatCurrency((pool.totalSize * 0.2 * pool.performanceFee) / 100)} SOL
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PoolAnalyticsModal;